import { diceSteps } from '../../play/daba/misc.js';

// Central buff engine: target/bonus-type definitions and stacking resolution.
// A buff is { id, name, active, tab, durationValue, durationUnit, remainingRounds, effects: [] }.
// tab is a string id; 'main' is the default. Custom tab names live on characterData.buffTabs.
// An effect is { id, amount, category, target, bonusType }.
// Durations are tracked in rounds: 1 minute = 10 rounds, 1 hour = 600 rounds.

const MAIN_BUFF_TAB = 'main';

function getBuffTab(buff) {
  return (buff && buff.tab) || MAIN_BUFF_TAB;
}

const BONUS_TYPES = [
  { value: 'alchemical', text: 'Alchemical', stacks: true },
  { value: 'armor', text: 'Armor', stacks: false, acOnly: true },
  { value: 'stackingArmor', text: 'Armor (Stacking)', stacks: true, acOnly: true },
  { value: 'circumstance', text: 'Circumstance', stacks: true },
  { value: 'competence', text: 'Competence', stacks: false },
  { value: 'deflection', text: 'Deflection', stacks: false, acOnly: true },
  { value: 'dodge', text: 'Dodge', stacks: true, acOnly: true },
  { value: 'enhancement', text: 'Enhancement', stacks: false },
  { value: 'inherent', text: 'Inherent', stacks: false },
  { value: 'insight', text: 'Insight', stacks: false },
  { value: 'luck', text: 'Luck', stacks: false },
  { value: 'morale', text: 'Morale', stacks: false },
  { value: 'naturalArmor', text: 'Natural Armor', stacks: false, acOnly: true },
  { value: 'stackingNaturalArmor', text: 'Natural Armor (Stacking)', stacks: true, acOnly: true },
  { value: 'enhancedNaturalArmor', text: 'Enhanced Natural Armor', stacks: false, acOnly: true },
  { value: 'profane', text: 'Profane', stacks: false },
  { value: 'racial', text: 'Racial', stacks: false },
  { value: 'resistance', text: 'Resistance', stacks: false },
  { value: 'sacred', text: 'Sacred', stacks: false },
  { value: 'shield', text: 'Shield', stacks: false, acOnly: true },
  { value: 'size', text: 'Size', stacks: false },
  { value: 'trait', text: 'Trait', stacks: false },
  { value: 'untyped', text: 'Untyped', stacks: true },
];

const DAMAGE_DICE_BONUS_TYPES = [
  { value: 'effective', text: 'Effective', stacks: false },
  { value: 'actual', text: 'Actual', stacks: false },
  { value: 'stacking', text: 'Stacking', stacks: true },
];

const BONUS_TYPE_MAP = BONUS_TYPES.concat(DAMAGE_DICE_BONUS_TYPES).reduce((map, type) => {
  map[type.value] = type;
  return map;
}, {});

const CATEGORIES = [
  { value: 'abilityScore', text: 'Ability Score' },
  { value: 'offense', text: 'Offense' },
  { value: 'defense', text: 'Defense' },
  { value: 'skills', text: 'Skills' },
];

const STATIC_TARGETS = {
  abilityScore: [
    { value: 'strength', text: 'Strength' },
    { value: 'dexterity', text: 'Dexterity' },
    { value: 'constitution', text: 'Constitution' },
    { value: 'intelligence', text: 'Intelligence' },
    { value: 'wisdom', text: 'Wisdom' },
    { value: 'charisma', text: 'Charisma' },
  ],
  offense: [
    { value: 'attack', text: 'Attack' },
    { value: 'damage', text: 'Damage' },
    { value: 'precision', text: 'Precision' },
    { value: 'nonMultipliedDamage', text: 'Non-Multiplied' },
    { value: 'criticalDamage', text: 'Critical Damage' },
    { value: 'damageDice', text: 'Dice Size' },
    { value: 'criticalConfirmation', text: 'Crit Confirmation' },
    { value: 'cmb', text: 'CMB' },
  ],
  defense: [
    { value: 'hitPoints', text: 'Hit Points' },
    { value: 'ac', text: 'AC' },
    { value: 'dr', text: 'DR' },
    { value: 'fortitude', text: 'Fortitude' },
    { value: 'reflex', text: 'Reflex' },
    { value: 'will', text: 'Will' },
    { value: 'cmd', text: 'CMD' },
  ],
};

// AC bonus types that do not carry over into CMD (nor Touch AC)
const AC_TYPES_EXCLUDED_FROM_CMD = [
  'armor',
  'stackingArmor',
  'armorStacking',
  'shield',
  'naturalArmor',
  'stackingNaturalArmor',
  'naturalArmorStacking',
  'enhancedNaturalArmor',
];

// Armor, shield, and natural armor bonuses never apply to Touch AC.
// The alternate *Stacking keys are retained for compatibility with any
// previously created/imported buffs that used the opposite word order.
const AC_TYPES_EXCLUDED_FROM_TOUCH = new Set(AC_TYPES_EXCLUDED_FROM_CMD);

const ROUNDS_PER_MINUTE = 10;
const ROUNDS_PER_HOUR = 600;

const DURATION_UNITS = [
  { value: 'rounds', text: 'Rounds', rounds: 1 },
  { value: 'minutes', text: 'Minutes', rounds: ROUNDS_PER_MINUTE },
  { value: 'hours', text: 'Hours', rounds: ROUNDS_PER_HOUR },
];

const DURATION_UNIT_MAP = DURATION_UNITS.reduce((map, unit) => {
  map[unit.value] = unit;
  return map;
}, {});

// Total duration of a buff in rounds, or null when no duration is set.
function getDurationRounds(buff) {
  if (!buff) return null;
  const value = parseInt(buff.durationValue);
  if (isNaN(value) || value <= 0) return null;
  const unit = DURATION_UNIT_MAP[buff.durationUnit] || DURATION_UNIT_MAP.rounds;
  return value * unit.rounds;
}

function formatDuration(rounds) {
  if (rounds === null || rounds === undefined || rounds === '') return '';
  const total = parseInt(rounds);
  if (isNaN(total)) return '';
  if (total <= 0) return 'Expired';
  const hours = Math.floor(total / ROUNDS_PER_HOUR);
  const minutes = Math.floor((total % ROUNDS_PER_HOUR) / ROUNDS_PER_MINUTE);
  const remainder = total % ROUNDS_PER_MINUTE;
  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (remainder || parts.length === 0) parts.push(`${remainder}r`);
  return parts.join(' ');
}

function num(value) {
  const n = parseInt(value);
  return isNaN(n) ? 0 : n;
}

function formatModifier(n) {
  return n >= 0 ? `+${n}` : `${n}`;
}

const ABILITY_KEYS = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];

const ABILITY_ALIASES = {
  str: 'strength',
  dex: 'dexterity',
  con: 'constitution',
  int: 'intelligence',
  wis: 'wisdom',
  cha: 'charisma',
};

const ABILITY_SHORT_NAMES = {
  strength: 'Str',
  dexterity: 'Dex',
  constitution: 'Con',
  intelligence: 'Int',
  wisdom: 'Wis',
  charisma: 'Cha',
};

// Amounts can reference an ability modifier: "[Strength/0.5]" means the
// character's current Strength modifier times 0.5, rounded down.
// "[Strength]" defaults to times 1. Short names like "[Str/2]" also work.
const ABILITY_AMOUNT_REGEX = /^\[\s*([a-zA-Z]+)\s*(?:\/\s*(-?\d*\.?\d+)\s*)?\]$/;
const POINT_TRACKER_AMOUNT_REGEX = /^\[\s*\{\s*([^{}]+?)\s*\}\s*([+-]\s*\d+)?\s*(?:\/\s*(-?\d*\.?\d+)\s*)?\]$/;

function parseAbilityAmount(text) {
  if (typeof text !== 'string') return null;
  const match = text.trim().match(ABILITY_AMOUNT_REGEX);
  if (!match) return null;
  const name = match[1].toLowerCase();
  const ability = ABILITY_ALIASES[name] || name;
  if (!ABILITY_KEYS.includes(ability)) return null;
  const multiplier = match[2] !== undefined ? parseFloat(match[2]) : 1;
  return { ability, multiplier: isNaN(multiplier) ? 1 : multiplier };
}

// "[{Deadly Juggernaut}+3/0.5]" means the current value of the named
// point tracker times 0.5, rounded down, then plus 3.
function parsePointTrackerAmount(text) {
  if (typeof text !== 'string') return null;
  const match = text.trim().match(POINT_TRACKER_AMOUNT_REGEX);
  if (!match) return null;
  const trackerName = match[1].trim();
  if (!trackerName) return null;
  const offset = match[2] === undefined ? 0 : parseInt(match[2].replace(/\s+/g, ''));
  const multiplier = match[3] === undefined ? 1 : parseFloat(match[3]);
  return {
    trackerName,
    offset: isNaN(offset) ? 0 : offset,
    multiplier: isNaN(multiplier) ? 1 : multiplier,
  };
}

// Evaluate an effect's amount. abilityModifierOverrides, when provided, is a
// { strength: mod, ... } map used instead of the stored (buffed) modifiers —
// used while recalculating the ability scores themselves to avoid feedback
// loops from self-referencing buffs.
function getEffectAmount(effect, characterData, abilityModifierOverrides, pointTrackers = []) {
  const abilityAmount = parseAbilityAmount(effect.amount);
  const trackerAmount = parsePointTrackerAmount(effect.amount);
  if (!abilityAmount && !trackerAmount) return num(effect.amount);

  if (trackerAmount) {
    const tracker = (pointTrackers || []).find(candidate =>
      candidate
      && String(candidate.name || '').trim().toLowerCase() === trackerAmount.trackerName.toLowerCase()
    );
    if (!tracker) return 0;
    const trackerValue = parseFloat(tracker.value);
    if (isNaN(trackerValue)) return 0;
    return Math.floor(trackerValue * trackerAmount.multiplier) + trackerAmount.offset;
  }

  let modifier = 0;
  if (abilityModifierOverrides && abilityAmount.ability in abilityModifierOverrides) {
    modifier = abilityModifierOverrides[abilityAmount.ability] || 0;
  } else if (characterData && characterData.abilities && characterData.abilities[abilityAmount.ability]) {
    modifier = characterData.abilities[abilityAmount.ability].modifiedModifier || 0;
  }
  return Math.floor(modifier * abilityAmount.multiplier);
}

function getSkillTargetKey(skill) {
  if (!skill) return '';
  return skill.name + (skill.specific ? ` (${skill.specific})` : '');
}

function getSkillTargetOptions(characterData) {
  const options = [{ value: 'all', text: 'All Skills' }];
  if (!characterData || !characterData.skills) return options;
  const seen = new Set();
  const allSkills = (characterData.skills.adventuring || []).concat(characterData.skills.background || []);
  allSkills.forEach(skill => {
    const key = getSkillTargetKey(skill);
    if (key && !seen.has(key)) {
      seen.add(key);
      options.push({ value: key, text: key });
    }
  });
  return options;
}

function getTargetOptions(category, characterData) {
  if (category === 'skills') return getSkillTargetOptions(characterData);
  return STATIC_TARGETS[category] || [];
}

function getBonusTypeOptions(category, target) {
  if (category === 'offense' && target === 'damageDice') {
    return DAMAGE_DICE_BONUS_TYPES.map(type => ({
      value: type.value,
      text: type.text,
    }));
  }
  const acAllowed = category === 'defense' && target === 'ac';
  return BONUS_TYPES
    .filter(type => acAllowed || !type.acOnly)
    .map(type => ({
      value: type.value,
      text: `${type.text}${type.stacks && !/\(stacking\)$/i.test(type.text) ? ' (stacks)' : ''}`,
    }));
}

function isBonusTypeAllowed(category, target, bonusType) {
  const damageDiceType = DAMAGE_DICE_BONUS_TYPES.some(type => type.value === bonusType);
  if (category === 'offense' && target === 'damageDice') return damageDiceType;
  if (damageDiceType) return false;
  const info = BONUS_TYPE_MAP[bonusType];
  if (!info) return false;
  if (info.acOnly) return category === 'defense' && target === 'ac';
  return true;
}

function getTargetText(effect, characterData) {
  if (!effect) return '';
  if (effect.category === 'skills') {
    return effect.target === 'all' ? 'All Skills' : effect.target;
  }
  const options = getTargetOptions(effect.category, characterData);
  const match = options.find(option => option.value === effect.target);
  return match ? match.text : effect.target;
}

function describeEffect(effect, characterData, pointTrackers = []) {
  const typeInfo = BONUS_TYPE_MAP[effect.bonusType];
  const typeText = typeInfo ? typeInfo.text : (effect.bonusType || 'Untyped');
  const abilityAmount = parseAbilityAmount(effect.amount);
  const trackerAmount = parsePointTrackerAmount(effect.amount);
  const damageExpression = !abilityAmount && !trackerAmount && isDamageExpressionTarget(effect.target)
    ? parseDamageExpression(effect.amount)
    : null;
  let amountText = damageExpression
    ? formatDamageExpression(damageExpression.dice, damageExpression.modifier)
    : formatModifier(getEffectAmount(effect, characterData, null, pointTrackers));
  if (damageExpression && amountText && !amountText.startsWith('-')) amountText = `+${amountText}`;
  if (abilityAmount) {
    amountText += ` (${ABILITY_SHORT_NAMES[abilityAmount.ability]}\u00d7${abilityAmount.multiplier})`;
  } else if (trackerAmount) {
    const offsetText = trackerAmount.offset
      ? formatModifier(trackerAmount.offset)
      : '';
    amountText += ` ({${trackerAmount.trackerName}}\u00d7${trackerAmount.multiplier}${offsetText})`;
  }
  return `${amountText} ${typeText} to ${getTargetText(effect, characterData)}`;
}

function getActiveEffects(buffs) {
  const effects = [];
  (buffs || []).forEach(buff => {
    if (!buff || !buff.active) return;
    (buff.effects || []).forEach(effect => effects.push(effect));
  });
  return effects;
}

// All effects currently applying to the character: active personal buffs
// plus active common statuses.
function getAllActiveEffects(characterData) {
  if (!characterData) return [];
  return getActiveEffects(characterData.buffs).concat(getActiveStatusEffects(characterData));
}

function getEffectsFor(characterData, category, targets) {
  const targetList = Array.isArray(targets) ? targets : [targets];
  return getAllActiveEffects(characterData).filter(effect =>
    effect.category === category && targetList.includes(effect.target)
  );
}

// Resolve a set of effects into per-type deltas honoring stacking rules.
// Stackable types sum everything. Non-stacking types apply only the highest
// positive bonus (compared against any matching typed bonus already on the
// sheet, via existingTyped), while penalties always stack.
// characterData is needed to evaluate ability-based amounts like "[Str/0.5]".
function resolveEffects(
  effects,
  existingTyped = {},
  characterData = null,
  abilityModifierOverrides = null,
  pointTrackers = []
) {
  const grouped = {};
  (effects || []).forEach(effect => {
    const amount = getEffectAmount(effect, characterData, abilityModifierOverrides, pointTrackers);
    if (!amount) return;
    const type = effect.bonusType || 'untyped';
    (grouped[type] = grouped[type] || []).push(amount);
  });

  const byType = {};
  let total = 0;
  Object.keys(grouped).forEach(type => {
    const info = BONUS_TYPE_MAP[type] || { stacks: true };
    let delta = 0;
    if (info.stacks) {
      delta = grouped[type].reduce((sum, n) => sum + n, 0);
    } else {
      delta = grouped[type].filter(n => n < 0).reduce((sum, n) => sum + n, 0);
      const positives = grouped[type].filter(n => n > 0);
      if (positives.length) {
        const best = Math.max(...positives);
        const existing = Math.max(0, num(existingTyped[type]));
        delta += Math.max(0, best - existing);
      }
    }
    byType[type] = delta;
    total += delta;
  });
  return { byType, total };
}

const DAMAGE_EXPRESSION_TARGETS = new Set([
  'damage',
  'precision',
  'nonMultipliedDamage',
  'criticalDamage',
]);

function isDamageExpressionTarget(target) {
  return DAMAGE_EXPRESSION_TARGETS.has(target);
}

// Parse a damage expression into signed dice terms and one flat modifier.
// Returns null when any part of the input is not a dice term or whole number.
function parseDamageExpression(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().replace(/\s+/g, '');
  if (!text) return null;
  if (!/^[+-]?(?:\d+d\d+|\d+)(?:[+-](?:\d+d\d+|\d+))*$/i.test(text)) return null;
  const terms = text.match(/[+-]?(?:\d+d\d+|\d+)/gi) || [];

  const dice = [];
  let modifier = 0;
  terms.forEach(term => {
    if (/d/i.test(term)) {
      dice.push(term.startsWith('+') ? term.substring(1) : term.toLowerCase());
    } else {
      modifier += parseInt(term) || 0;
    }
  });
  return { dice, modifier };
}

function formatDamageExpression(dice = [], modifier = 0) {
  let result = '';
  dice.forEach(term => {
    const text = String(term);
    if (!result) {
      result = text.startsWith('+') ? text.substring(1) : text;
    } else {
      result += /^[+-]/.test(text) ? text : `+${text}`;
    }
  });
  if (modifier) {
    result += result && modifier > 0 ? `+${modifier}` : String(modifier);
  }
  return result || '0';
}

function combineDamageExpressions(...values) {
  const dice = [];
  let modifier = 0;
  values.forEach(value => {
    if (value === null || value === undefined || value === '') return;
    const parsed = typeof value === 'object' && Array.isArray(value.dice)
      ? value
      : parseDamageExpression(value);
    if (!parsed) return;
    dice.push(...parsed.dice);
    modifier += parsed.modifier;
  });
  return formatDamageExpression(dice, modifier);
}

function getDamageExpressionBuff(characterData, target, existingTyped = {}, pointTrackers = []) {
  const dice = [];
  const modifierEffects = [];
  getEffectsFor(characterData, 'offense', target).forEach(effect => {
    const abilityAmount = parseAbilityAmount(effect.amount);
    const trackerAmount = parsePointTrackerAmount(effect.amount);
    if (abilityAmount || trackerAmount) {
      modifierEffects.push(effect);
      return;
    }
    const parsed = parseDamageExpression(effect.amount);
    if (!parsed) return;
    dice.push(...parsed.dice);
    if (parsed.modifier) {
      modifierEffects.push({ ...effect, amount: String(parsed.modifier) });
    }
  });
  const modifier = resolveEffects(
    modifierEffects,
    existingTyped,
    characterData,
    null,
    pointTrackers
  ).total;
  return {
    dice,
    modifier,
    text: formatDamageExpression(dice, modifier),
  };
}

function damageExistingTyped(damageBonus) {
  const db = damageBonus || {};
  return {
    enhancement: db.enhancementBonus,
    luck: db.luckBonus,
    morale: db.moraleBonus,
    profane: db.profaneBonus,
    sacred: db.sacredBonus,
    insight: db.insightBonus,
    competence: db.competenceBonus,
    size: db.sizeModifier,
  };
}

function getDamageBuffParts(characterData, damageBonus, pointTrackers = []) {
  if (!characterData) return { dice: [], modifier: 0, text: '0' };
  return getDamageExpressionBuff(
    characterData,
    'damage',
    damageExistingTyped(damageBonus),
    pointTrackers
  );
}

function getDamageSectionBuffText(characterData, target, pointTrackers = []) {
  if (!characterData || !['precision', 'nonMultipliedDamage', 'criticalDamage'].includes(target)) {
    return '0';
  }
  return getDamageExpressionBuff(characterData, target, {}, pointTrackers).text;
}

function resolveDamageDiceType(amounts, stacks) {
  if (stacks) return amounts.reduce((sum, amount) => sum + amount, 0);
  const penalties = amounts.filter(amount => amount < 0).reduce((sum, amount) => sum + amount, 0);
  const highestBonus = Math.max(0, ...amounts.filter(amount => amount > 0));
  return penalties + highestBonus;
}

function getDamageDiceStepTotal(characterData) {
  if (!characterData) return 0;
  const grouped = { effective: [], actual: [], stacking: [] };
  getEffectsFor(characterData, 'offense', 'damageDice').forEach(effect => {
    if (!Object.prototype.hasOwnProperty.call(grouped, effect.bonusType)) return;
    const amount = getEffectAmount(effect, characterData);
    if (amount) grouped[effect.bonusType].push(amount);
  });
  return resolveDamageDiceType(grouped.effective, false)
    + resolveDamageDiceType(grouped.actual, false)
    + resolveDamageDiceType(grouped.stacking, true);
}

function damageDiceForStep(step) {
  if (step < diceSteps.length) return diceSteps[step].split(',')[0];

  const overSteps = step - diceSteps.length;
  let dieCount = 32;
  let dieSize = 6;
  let doubledCount = 0;
  for (let remaining = overSteps; remaining > 0; remaining--) {
    if (dieSize === 8) {
      if (doubledCount) {
        dieCount += doubledCount;
        doubledCount = 0;
      } else {
        doubledCount = dieCount / 2;
        dieCount += doubledCount;
      }
      dieSize = 6;
    } else {
      dieSize = 8;
    }
  }
  return `${dieCount}d${dieSize}`;
}

function stepDamageDiceTerm(term, steps) {
  const text = String(term).toLowerCase();
  const sign = text.startsWith('-') ? '-' : '';
  const unsigned = text.replace(/^[+-]/, '');
  const index = diceSteps.findIndex(entry => entry.split(',').includes(unsigned));
  if (index === -1) return text;
  const targetStep = Math.max(0, index + steps);
  return sign + damageDiceForStep(targetStep);
}

function stepBaseDamageDice(baseDice, steps) {
  const amount = parseInt(steps) || 0;
  if (!amount || !baseDice) return baseDice || '';
  const text = String(baseDice).trim().replace(/\s+/g, '');
  if (!/^[+-]?(?:\d+d\d+|\d+)(?:[+-](?:\d+d\d+|\d+))*$/i.test(text)) return baseDice;
  const terms = text.match(/[+-]?(?:\d+d\d+|\d+)/gi) || [];

  const stepped = terms.map(term => {
    const unsigned = term.replace(/^[+-]/, '');
    return /d/i.test(unsigned) || unsigned === '1'
      ? stepDamageDiceTerm(term, amount)
      : term;
  });
  return formatDamageExpression(stepped, 0);
}

function getAbilityBuffBreakdown(
  characterData,
  abilityKey,
  abilityModifierOverrides,
  pointTrackers = []
) {
  if (!characterData) return { byType: {}, total: 0 };
  return resolveEffects(
    getEffectsFor(characterData, 'abilityScore', abilityKey),
    {},
    characterData,
    abilityModifierOverrides,
    pointTrackers
  );
}

function getAbilityBuffTotal(characterData, abilityKey, abilityModifierOverrides, pointTrackers = []) {
  return getAbilityBuffBreakdown(
    characterData,
    abilityKey,
    abilityModifierOverrides,
    pointTrackers
  ).total;
}

function getAcBuffByType(characterData, pointTrackers = []) {
  if (!characterData) return {};
  const ac = characterData.defense.ac;
  const existing = {
    armor: ac.armorBonus,
    shield: ac.shieldBonus,
    naturalArmor: ac.naturalArmor,
    enhancedNaturalArmor: ac.enhancedNaturalArmor,
    deflection: ac.deflection,
    morale: ac.moraleBonus,
    profane: ac.profaneBonus,
    sacred: ac.sacredBonus,
    insight: ac.insightBonus,
  };
  return resolveEffects(
    getEffectsFor(characterData, 'defense', 'ac'),
    existing,
    characterData,
    null,
    pointTrackers
  ).byType;
}

function getAcBuffTotal(characterData, pointTrackers = []) {
  const byType = getAcBuffByType(characterData, pointTrackers);
  return Object.values(byType).reduce((sum, n) => sum + n, 0);
}

function getNonTouchAcBuffTotal(characterData, pointTrackers = []) {
  const byType = getAcBuffByType(characterData, pointTrackers);
  return Object.entries(byType).reduce((total, [type, amount]) =>
    total + (AC_TYPES_EXCLUDED_FROM_TOUCH.has(type) ? amount : 0), 0);
}

function getCmdBuffTotal(characterData, pointTrackers = []) {
  if (!characterData) return 0;
  const ac = characterData.defense.ac;
  const effects = getEffectsFor(characterData, 'defense', 'cmd').concat(
    getEffectsFor(characterData, 'defense', 'ac')
      .filter(effect => !AC_TYPES_EXCLUDED_FROM_CMD.includes(effect.bonusType))
  );
  const existing = {
    deflection: ac.deflection,
    morale: ac.moraleBonus,
    profane: ac.profaneBonus,
    sacred: ac.sacredBonus,
    insight: ac.insightBonus,
  };
  return resolveEffects(effects, existing, characterData, null, pointTrackers).total;
}

function getSaveBuffTotal(characterData, saveKey, pointTrackers = []) {
  if (!characterData) return 0;
  return resolveEffects(
    getEffectsFor(characterData, 'defense', saveKey),
    {},
    characterData,
    null,
    pointTrackers
  ).total;
}

function getHpBuffTotal(characterData, pointTrackers = []) {
  if (!characterData) return 0;
  return resolveEffects(
    getEffectsFor(characterData, 'defense', 'hitPoints'),
    {},
    characterData,
    null,
    pointTrackers
  ).total;
}

function getDrBuffTotal(characterData, pointTrackers = []) {
  if (!characterData) return 0;
  return resolveEffects(
    getEffectsFor(characterData, 'defense', 'dr'),
    {},
    characterData,
    null,
    pointTrackers
  ).total;
}

function attackExistingTyped(characterData) {
  const attackBonus = (characterData.offense && characterData.offense.attackBonus) || {};
  return {
    enhancement: attackBonus.enhancementBonus,
    luck: attackBonus.luckBonus,
    morale: attackBonus.moraleBonus,
    profane: attackBonus.profaneBonus,
    sacred: attackBonus.sacredBonus,
    insight: attackBonus.insightBonus,
    competence: attackBonus.competenceBonus,
    size: attackBonus.sizeModifier,
  };
}

function getAttackBuffTotal(characterData, pointTrackers = []) {
  if (!characterData) return 0;
  return resolveEffects(
    getEffectsFor(characterData, 'offense', 'attack'),
    attackExistingTyped(characterData),
    characterData,
    null,
    pointTrackers
  ).total;
}

function getCriticalConfirmationBuffTotal(characterData, pointTrackers = []) {
  if (!characterData) return 0;
  const existing = attackExistingTyped(characterData);
  const attackEffects = getEffectsFor(characterData, 'offense', 'attack');
  const confirmationEffects = getEffectsFor(characterData, 'offense', 'criticalConfirmation');
  const attackTotal = resolveEffects(
    attackEffects,
    existing,
    characterData,
    null,
    pointTrackers
  ).total;
  const confirmationTotal = resolveEffects(
    attackEffects.concat(confirmationEffects),
    existing,
    characterData,
    null,
    pointTrackers
  ).total;
  return confirmationTotal - attackTotal;
}

// Attack buffs flow into CMB as well; CMB totals exclude the sheet's
// enhancement/size attack fields, so those types resolve against nothing.
function getCmbBuffTotal(characterData, pointTrackers = []) {
  if (!characterData) return 0;
  const attackBonus = (characterData.offense && characterData.offense.attackBonus) || {};
  const effects = getEffectsFor(characterData, 'offense', ['cmb', 'attack']);
  const existing = {
    luck: attackBonus.luckBonus,
    morale: attackBonus.moraleBonus,
    profane: attackBonus.profaneBonus,
    sacred: attackBonus.sacredBonus,
    insight: attackBonus.insightBonus,
    competence: attackBonus.competenceBonus,
  };
  return resolveEffects(effects, existing, characterData, null, pointTrackers).total;
}

function getDamageBuffTotal(characterData, damageBonus, pointTrackers = []) {
  return getDamageBuffParts(characterData, damageBonus, pointTrackers).modifier;
}

function getSkillBuffTotal(skill, characterData, pointTrackers = []) {
  if (!characterData) return 0;
  const key = getSkillTargetKey(skill);
  const effects = getAllActiveEffects(characterData).filter(effect =>
    effect.category === 'skills' && (effect.target === 'all' || effect.target === key)
  );
  return resolveEffects(effects, {}, characterData, null, pointTrackers).total;
}

// Common statuses are defined in code (not saved to the sheet) so their
// effects stay up to date; only per-character state (active/remainingRounds)
// is persisted, under characterData.commonStatuses[id].
function statusEffect(id, amount, category, target, bonusType = 'untyped') {
  return { id, amount, category, target, bonusType };
}

function saveEffects(idPrefix, amount, bonusType = 'untyped') {
  return ['fortitude', 'reflex', 'will'].map(save =>
    statusEffect(`${idPrefix}-${save}`, amount, 'defense', save, bonusType)
  );
}

const COMMON_STATUSES = [
  { id: 'sickened', name: 'Sickened', effects: [
    statusEffect('sickened-attack', -2, 'offense', 'attack'),
    statusEffect('sickened-damage', -2, 'offense', 'damage'),
    ...saveEffects('sickened', -2),
    statusEffect('sickened-skills', -2, 'skills', 'all'),
  ]},
  { id: 'prone', name: 'Prone', effects: [
    statusEffect('prone-attack', -4, 'offense', 'attack'),
    statusEffect('prone-ac', -4, 'defense', 'ac'),
  ]},
  { id: 'shaken', name: 'Shaken', effects: [
    statusEffect('shaken-attack', -2, 'offense', 'attack'),
    ...saveEffects('shaken', -2),
    statusEffect('shaken-skills', -2, 'skills', 'all'),
  ]},
  { id: 'entangled', name: 'Entangled', effects: [
    statusEffect('entangled-attack', -2, 'offense', 'attack'),
    statusEffect('entangled-dex', -4, 'abilityScore', 'dexterity'),
  ]},
  { id: 'grappled', name: 'Grappled', effects: [
    statusEffect('grappled-dex', -4, 'abilityScore', 'dexterity'),
    statusEffect('grappled-attack', -2, 'offense', 'attack'),
  ]},
  { id: 'fatigued', name: 'Fatigued', effects: [
    statusEffect('fatigued-str', -2, 'abilityScore', 'strength'),
    statusEffect('fatigued-dex', -2, 'abilityScore', 'dexterity'),
  ]},
  { id: 'exhausted', name: 'Exhausted', effects: [
    statusEffect('exhausted-str', -6, 'abilityScore', 'strength'),
    statusEffect('exhausted-dex', -6, 'abilityScore', 'dexterity'),
  ]},
  { id: 'dazzled', name: 'Dazzled', effects: [
    statusEffect('dazzled-attack', -1, 'offense', 'attack'),
  ]},
  { id: 'tanner', name: 'Tanner', effects: [
    statusEffect('tanner-attack', 5, 'offense', 'attack', 'competence'),
    statusEffect('tanner-damage', 5, 'offense', 'damage', 'competence'),
    ...saveEffects('tanner', 5, 'morale'),
  ]},
  { id: 'tannerEnhanced', name: 'Tanner Enhanced', effects: [
    statusEffect('tannerEnhanced-attack', 5, 'offense', 'attack', 'competence'),
    statusEffect('tannerEnhanced-damage', 5, 'offense', 'damage', 'competence'),
    ...saveEffects('tannerEnhanced', 10, 'morale'),
  ]},
];

const COMMON_STATUS_MAP = COMMON_STATUSES.reduce((map, status) => {
  map[status.id] = status;
  return map;
}, {});

function getStatusState(characterData, statusId) {
  const states = characterData && characterData.commonStatuses;
  return (states && states[statusId]) || null;
}

function getActiveStatusEffects(characterData) {
  const effects = [];
  COMMON_STATUSES.forEach(status => {
    const state = getStatusState(characterData, status.id);
    if (state && state.active) status.effects.forEach(effect => effects.push(effect));
  });
  return effects;
}

function createEffect() {
  return { id: Math.random(), amount: '', category: '', target: '', bonusType: '' };
}

function createBuff(tab = MAIN_BUFF_TAB) {
  return {
    id: Math.random(),
    name: '',
    active: false,
    tab: tab || MAIN_BUFF_TAB,
    durationValue: '',
    durationUnit: 'rounds',
    remainingRounds: null,
    effects: [createEffect()],
  };
}

// Backfill tab / buffTabs on loaded character data so old sheets work
// without a DB migration.
function normalizeBuffTabs(characterData) {
  if (!characterData) return;
  if (!Array.isArray(characterData.buffTabs)) characterData.buffTabs = [];
  if (!Array.isArray(characterData.buffs)) characterData.buffs = [];
  const known = new Set([MAIN_BUFF_TAB, ...characterData.buffTabs]);
  characterData.buffs.forEach(buff => {
    if (!buff) return;
    if (!buff.tab) buff.tab = MAIN_BUFF_TAB;
    if (buff.tab !== MAIN_BUFF_TAB && !known.has(buff.tab)) {
      characterData.buffTabs.push(buff.tab);
      known.add(buff.tab);
    }
  });
}

export default {
  MAIN_BUFF_TAB,
  BONUS_TYPES,
  DAMAGE_DICE_BONUS_TYPES,
  BONUS_TYPE_MAP,
  CATEGORIES,
  DURATION_UNITS,
  ROUNDS_PER_MINUTE,
  ROUNDS_PER_HOUR,
  getBuffTab,
  normalizeBuffTabs,
  getDurationRounds,
  formatDuration,
  getTargetOptions,
  getBonusTypeOptions,
  isBonusTypeAllowed,
  getSkillTargetKey,
  describeEffect,
  formatModifier,
  parseAbilityAmount,
  parsePointTrackerAmount,
  isDamageExpressionTarget,
  parseDamageExpression,
  formatDamageExpression,
  combineDamageExpressions,
  getEffectAmount,
  getActiveEffects,
  getAllActiveEffects,
  getEffectsFor,
  COMMON_STATUSES,
  COMMON_STATUS_MAP,
  getStatusState,
  getActiveStatusEffects,
  resolveEffects,
  getAbilityBuffBreakdown,
  getAbilityBuffTotal,
  getAcBuffByType,
  getAcBuffTotal,
  getNonTouchAcBuffTotal,
  getCmdBuffTotal,
  getSaveBuffTotal,
  getHpBuffTotal,
  getDrBuffTotal,
  getAttackBuffTotal,
  getCriticalConfirmationBuffTotal,
  getCmbBuffTotal,
  getDamageBuffParts,
  getDamageBuffTotal,
  getDamageSectionBuffText,
  getDamageDiceStepTotal,
  stepBaseDamageDice,
  getSkillBuffTotal,
  createBuff,
  createEffect,
};
